import { DialogWithForm } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { type IntakeCase, usePostAttachDocumentsMutation } from 'store/api/intakeCaseApi';

interface AttachDocumentsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    intakeCase: Pick<IntakeCase, 'intakeCaseId' | 'intakeStatus'>;
}

export const AttachDocumentsDialog = ({
    isOpen,
    onClose,
    intakeCase,
}: AttachDocumentsDialogProps) => {
    const [postAttachDocuments] = usePostAttachDocumentsMutation();

    const validationSchema = Yup.object({
        documents: FileUploadFormik.validator()
            .required(validationStrings.requiredField.errorMessage)
            .min(1, validationStrings.requiredField.errorMessage),
        uploadedDocuments: Yup.array().required().min(1),
    });

    return (
        <Formik
            initialValues={{} as Yup.InferType<typeof validationSchema>}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                await postAttachDocuments({
                    intakeCaseId: intakeCase.intakeCaseId,
                    files: values.uploadedDocuments as File[],
                }).unwrap();
                SuccessToast({ message: 'Documents attached successfully.' });

                onClose();
                resetForm();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Attach Documents"
                    width={600}
                    isOpen={isOpen}
                    primaryButtonLabel="Attach Documents"
                    isPrimaryButtonDisabled={
                        formik.isSubmitting || !(formik.dirty && formik.isValid)
                    }
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={(e) => {
                        onClose();
                        formik.handleReset(e);
                    }}
                    dialogContent={
                        <FileUploadFormik
                            id="documents"
                            label="Documents"
                            required
                            accept="application/pdf"
                            onAddFiles={async (files) => {
                                await formik.setFieldValue('uploadedDocuments', [
                                    ...((formik.values.uploadedDocuments ?? []) as File[]),
                                    ...files,
                                ]);
                            }}
                            onRemoveFile={async (filename) => {
                                await formik.setFieldValue(
                                    'uploadedDocuments',
                                    formik.values.uploadedDocuments?.filter(
                                        (file: File) => file.name !== filename,
                                    ),
                                );
                            }}
                        />
                    }
                />
            )}
        </Formik>
    );
};
